<template>
  <component
    :is="tag"
    class="def-buy-secondary"
    :disabled="disabled || isRequestSend"
    @click="handlerBuy"
  >
    <!-- product in basket -->
    <slot v-if="basketStore.inBasket(id)" name="inBasket">
      <DefButtonPrimary :disabled="disabled || isRequestSend">
        <svg class="def-buy-secondary__icon"><use xlink:href="#svg-check"></use></svg>
      </DefButtonPrimary>
    </slot>

    <!-- add product to basket -->
    <slot v-else>
      <DefButtonPrimary :disabled="disabled || isRequestSend">
        <svg class="def-buy-secondary__icon"><use xlink:href="#svg-basket"></use></svg>
      </DefButtonPrimary>
    </slot>
  </component>
</template>

<script>
import gsap from 'gsap';
import useModal from '@use/useModal/useModal';
import useBasketModal from '@use/useBasketModal';
import { useBasketStore } from '@/storePinia/stores/basket';
import useLoader from '@use/useLoader/useLoader';

import Analytics from '@/utils/Analytics/Analytics';
import DefSKU from '@components/Def/DefSKU/DefSKU.vue';
import DefButtonPrimary from '@components/Def/DefButtonPrimary/DefButtonPrimary.vue';
import { scrollPageTo } from '@/utils/helpers';

export default {
  components: { DefButtonPrimary },
  props: {
    result: {
      type: Object,
    },
    disabled: {
      type: Boolean,
    },
    tag: {
      type: String,
      default: 'button',
    },
    added: {
      type: Function,
    },
  },
  data() {
    return {
      isRequestSend: false,
      modal: useModal(),
      basketModal: useBasketModal(),
      basketStore: useBasketStore(),
      loader: useLoader(),
    };
  },
  computed: {
    id() {
      return this.result?.offer?.id || this.result?.id;
    },
    SIZES() {
      return this.result?.skuMatrix?.SIZES?.items;
    },
    isProductWithCustomWeight() {
      return this.result?.isProductWithCustomWeight;
    },
    getActiveSize() {
      return this.SIZES?.find((item) => item.active);
    },
    // вага, передана разом з товаром (наприклад, з намеку), яку треба зробити активною в модалці
    selectedWeightParams() {
      const jewelWeight = this.result?.jewelWeight;

      if (!this.isProductWithCustomWeight || !jewelWeight?.barcode) return null;

      return {
        isProductWithCustomWeight: true,
        price: jewelWeight.price,
        barcode: jewelWeight.barcode,
        weightOption: jewelWeight.weightOption,
        weight: jewelWeight.weight,
      };
    },
  },
  methods: {
    createAnimation() {
      try {
        const div = document.createElement('div');
        div.classList.add('buy-animation');
        div.style.backgroundImage = `url(${this.result.picture.src})`;
        div.style.zIndex = 2001;

        const baskets = document.querySelectorAll('.small-basket-control');

        if (!baskets.length) return;

        const smallBasket = baskets.length === 2 ? baskets[1] : baskets[0];
        const rect = smallBasket.getBoundingClientRect();

        document.body.appendChild(div);

        gsap
          .timeline({
            defaults: {
              duration: 1,
            },
            onComplete() {
              div.remove();
            },
          })
          .to(div, {
            top: innerHeight / 2,
            left: innerWidth / 2,
            width: 100,
            height: 100,
            xPercent: -50,
            yPercent: -100,
            ease: 'power1.in',
          })
          .to(div, {
            top: rect.top,
            left: rect.left,
            width: 50,
            height: 50,
            borderWidth: 1,
            xPercent: -20,
            yPercent: -20,
            ease: 'none',
          });
      } catch (err) {
        console.error(err);
      }
    },
    async handlerBuy() {
      if (!this.basketStore.inBasket(this.id)) {
        try {
          this.loader.start();
          const { status, data } = await axios.post(
            this.getActiveSize?.detailPage || this.result.detailPage,
            {
              api: 'Y',
              ...this.selectedWeightParams,
            }
          );

          if (data.product?.skuMatrix?.SIZES?.items || data?.product?.isProductWithCustomWeight) {
            this.modal.show(
              DefSKU,
              {
                result: data.product,
                isWeightSelected: !!this.selectedWeightParams,
                added: () => this.$emit('added'),
              },
              { width: '500px', zIndex: 10004 }
            );
          } else {
            this.isRequestSend = true;
            this.createAnimation();
            await this.basketStore.add({ id: this.id, count: this.result?.price?.quantity });
            Analytics.addToBasket(this.result);
            scrollPageTo(0);
            this.isRequestSend = false;
          }
        } catch (err) {
          console.error(err);
        } finally {
          this.loader.stop();
        }
      } else {
        await this.modal.hide();
        this.basketModal.open();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.def-buy-secondary {
  &__icon {
    display: block;
    width: 20px;
    height: 20px;
    fill: currentColor;
    flex-shrink: 0;
  }
  :deep(.def-button-primary) {
    width: 100%;
  }
}
</style>
